import { Box, BoxProps, SxProps, Theme } from "@mui/material";
import { Property } from "csstype";
import { BREAKPOINTS } from "src/theme";
import { TImageLoading } from "src/types/image.types";
import { buildUrl, IQueryParams } from "src/utils/responsive-image-util";

const BREAKPOINT_VALUES = Object.values(BREAKPOINTS);
const PICTURE_SX: SxProps<Theme> = { display: "contents" };
const IMAGE_SX: SxProps<Theme> = { maxWidth: "100%", height: "auto" };

export interface ISrc {
    queryParams?: IQueryParams;
    url?: string;
}

export interface IResponsiveImageProps extends Omit<BoxProps<"img">, "src" | "srcSet" | "alt" | "color" | "component"> {
    baseUrl: string;
    alt: string | null;
    onError?: () => void;
    srcSet?: ISrc[];
    loading?: TImageLoading;
    objectFit?: Property.ObjectFit | Property.ObjectFit[];
    objectPosition?: Property.ObjectPosition | Property.ObjectPosition[];
    sx?: SxProps<Theme>;
}

function buildMediaSources(breakpointSources: ISrc[]) {
    return breakpointSources
        .flatMap((src, index) => {
            const minWidth = BREAKPOINT_VALUES[index];
            if (!src || !minWidth) {
                return [];
            }

            return [{ src, minWidth }];
        })
        .reverse();
}

export const ResponsiveImage = ({
    baseUrl,
    alt,
    srcSet,
    loading = "lazy",
    onError,
    objectFit,
    objectPosition,
    sx,
    ...imageProps
}: IResponsiveImageProps) => {
    const [defaultSrc, ...breakpointSources] = srcSet ?? [];
    const mediaSources = buildMediaSources(breakpointSources);

    return (
        <Box component="picture" sx={PICTURE_SX}>
            {mediaSources.map(({ src, minWidth }) => (
                <source key={minWidth} media={`(min-width: ${minWidth})`} srcSet={buildUrl(src.url || baseUrl, src.queryParams)} />
            ))}
            <Box
                component="img"
                src={buildUrl(defaultSrc?.url || baseUrl, defaultSrc?.queryParams)}
                loading={loading}
                alt={alt ?? undefined}
                onError={onError}
                sx={[
                    IMAGE_SX,
                    objectFit ? { objectFit } : undefined,
                    objectPosition ? { objectPosition } : undefined,
                    ...(Array.isArray(sx) ? sx : [sx]),
                ]}
                {...imageProps}
            />
        </Box>
    );
};
